import { useState } from 'react';
import { ShoppingBag, Truck, CheckCircle } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BOOK_COVER = 'https://customer-assets.emergentagent.com/job_threads-store-109/artifacts/26nq4udt_6430819E-94EA-4DC6-9264-16266FE523B8.png';

export const BookPage = () => {
  const [processing, setProcessing] = useState(false);
  const [quantity, setQuantity] = useState(1);

  const handleBuyNow = async () => {
    setProcessing(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/book-checkout`, {
        quantity,
        origin_url: originUrl
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to initiate checkout');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="book-page">
      {/* Hero */}
      <div className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Book Cover */}
          <div className="flex justify-center lg:justify-end" data-testid="book-cover-container">
            <div className="relative w-full max-w-sm">
              <div className="aspect-[2/3] bg-black border border-border overflow-hidden shadow-2xl">
                <img
                  src={BOOK_COVER}
                  alt="The Weight Of The Helmet"
                  className="w-full h-full object-cover"
                  data-testid="book-cover-image"
                />
              </div>
            </div>
          </div>

          {/* Book Info */}
          <div className="lg:py-8">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-3">
              NEW RELEASE
            </p>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter font-bold leading-none mb-6"
              style={{ fontFamily: "'Oswald', sans-serif" }}
              data-testid="book-title"
            >
              THE WEIGHT OF<br />THE HELMET
            </h1>

            <p
              className="text-3xl font-bold mb-2"
              style={{ fontFamily: "'Oswald', sans-serif" }}
              data-testid="book-price"
            >
              $15.00
            </p>
            <div className="flex items-center gap-2 mb-8">
              <Truck size={16} className="text-primary" />
              <span className="text-sm text-primary font-bold uppercase tracking-wide">
                FREE SHIPPING
              </span>
            </div>

            <p className="text-muted-foreground leading-relaxed mb-8 max-w-md" data-testid="book-description">
              A story of brotherhood, resilience, and letting go. 
              By Quinton Washington. Behind the badge, the uniform, and the helmet 
              lies a burden few understand. This book pulls back the curtain on the 
              sacrifices, struggles, and strength of first responders.
            </p>

            {/* Quantity */}
            <div className="mb-8">
              <label className="block text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">
                QUANTITY
              </label>
              <div className="flex items-center border border-border w-fit">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-4 py-3 hover:bg-muted transition-colors text-lg font-bold"
                  data-testid="qty-decrease"
                >
                  -
                </button>
                <span className="px-6 py-3 text-lg font-bold min-w-[60px] text-center" data-testid="qty-value">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="px-4 py-3 hover:bg-muted transition-colors text-lg font-bold"
                  data-testid="qty-increase"
                >
                  +
                </button>
              </div>
            </div>

            {/* Buy Button */}
            <Button
              onClick={handleBuyNow}
              disabled={processing}
              className="w-full md:w-auto bg-white text-black hover:bg-primary hover:text-white transition-colors h-14 px-12 text-base font-bold uppercase tracking-wide"
              data-testid="buy-now-btn"
            >
              <ShoppingBag size={20} className="mr-2" />
              {processing ? 'PROCESSING...' : `BUY NOW — $${(15 * quantity).toFixed(2)}`}
            </Button>

            {/* Features */}
            <div className="mt-12 pt-8 border-t border-border space-y-3">
              <div className="flex items-center gap-3">
                <CheckCircle size={18} weight="fill" className="text-primary" />
                <span className="text-sm uppercase tracking-wide">Free shipping on all book orders</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle size={18} weight="fill" className="text-primary" />
                <span className="text-sm uppercase tracking-wide">Secure checkout powered by Stripe</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle size={18} weight="fill" className="text-primary" />
                <span className="text-sm uppercase tracking-wide">Ships within 3-5 business days</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
